import { Component, OnInit, computed, signal, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthenticationService } from '../../../shared/services/api/authentication/authentication.service';
import { PasswordValidationService } from '../../../shared/services/password-validation.service';
import { finalize, first } from 'rxjs/operators';

@Component({
  selector: 'app-reset-password',
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ResetPasswordComponent implements OnInit {
  readonly PASSWORD_REQUIREMENTS = this.passwordValidation.requirements;

  resetToken: string | null = null;
  password = signal('');
  confirmPassword = signal('');
  showPassword = signal(false);
  showConfirmPassword = signal(false);
  hasInteracted = signal(false);
  userLeftConfirmInput = signal(false);
  isSubmitting = signal(false);
  resetSuccess = signal(false);

  requirements = computed(() => this.passwordValidation.getRequirementsWithStatus(this.password()));

  allMet = computed(() => this.passwordValidation.allRequirementsMet(this.password()));

  strength = computed(() => this.passwordValidation.getPasswordStrength(this.password()));

  passwordsMatch = computed(() => {
    const pw = this.password();
    const cp = this.confirmPassword();
    return pw.length > 0 && cp.length > 0 && pw === cp;
  });

  confirmMismatch = computed(() => {
    const cp = this.confirmPassword();
    return this.userLeftConfirmInput() && cp.length > 0 && this.password() !== cp;
  });

  canSubmit = computed(() => this.allMet() && this.passwordsMatch() && !this.isSubmitting() && this.resetToken);

  constructor(
    private authService: AuthenticationService,
    private route: ActivatedRoute,
    private router: Router,
    private passwordValidation: PasswordValidationService
  ) {}

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      if (params['token']) {
        this.resetToken = params['token'];
        this.authService.checkResetPasswordToken(this.resetToken).subscribe(
          () => {},
          error => {
            switch (error?.status) {
              case 403: {
                this.router.navigate(['/login/reset-password-expired']).then();
                break;
              }
              default: {
                this.router.navigate(['/login/reset-password-incorrect']).then();
                break;
              }
            }
          }
        );
      } else {
        this.router.navigate(['/login']).then();
      }
    });
  }

  onPasswordInput(value: string): void {
    this.password.set(value);
    if (!this.hasInteracted()) this.hasInteracted.set(true);
  }

  onConfirmPasswordInput(value: string): void {
    this.confirmPassword.set(value);
  }

  toggleShowPassword(): void {
    this.showPassword.update(v => !v);
  }

  toggleShowConfirmPassword(): void {
    this.showConfirmPassword.update(v => !v);
  }

  onConfirmBlur(): void {
    this.userLeftConfirmInput.set(true);
  }

  onSubmit(): void {
    if (!this.canSubmit()) return;

    this.isSubmitting.set(true);

    this.authService
      .resetPassword(this.password(), this.resetToken || '')
      .pipe(first())
      .pipe(finalize(() => this.isSubmitting.set(false)))
      .subscribe(
        () => this.resetSuccess.set(true),
        error => {
          const status = error?.status;
          switch (status) {
            case 403: {
              this.router.navigate(['/login/reset-password-expired']).then();
              break;
            }
            case 404: {
              this.router.navigate(['/login/reset-password-expired']).then();
              break;
            }
            default: {
              console.error('Une erreur est survenue...404 ou 500');
              break;
            }
          }
        }
      );
  }
}
